import queryString from 'query-string';

// Libs
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { PERMISSIONS } from '@/lib/permissions/socialPermissions';

// Endpoints
import { OAUTH_NETWORKS } from '@/lib/routes/privateRoutes';

// Hooks
import useAcl from '@/utils/hooks/user/useAcl';
import useRouterQuery from '@/hooks/utils/useRouterQuery';

// Types
import type { StringifiableRecord } from 'query-string';
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

type GetOAuthUrlParams = {
    network: SocialProfileNetworkType;
    query?: StringifiableRecord;
    redirect?: {
        url?: string;
        query?: StringifiableRecord;
    };
};

const useNetworkOauthUrls = () => {
    const { hasAcl } = useAcl();
    const { deleteAllQueries } = useRouterQuery();

    const getRedirectUrl = (redirect: GetOAuthUrlParams['redirect']) => {
        const currentUrl = deleteAllQueries();
        return queryString.stringifyUrl(
            {
                url: redirect?.url ?? currentUrl,
                query: redirect?.query
            },
            { arrayFormat: 'bracket' }
        );
    };

    const getOauthUrl = ({ network, query, redirect }: GetOAuthUrlParams) => {
        const hasAccessToPromotedPost = hasAcl(aclFeatures.analytic.promoted_post.read);

        const redirectUrl = getRedirectUrl(redirect);

        const permissions = [
            ...(hasAccessToPromotedPost ? [PERMISSIONS.ADS] : []),
            PERMISSIONS.ANALYTICS,
            PERMISSIONS.SCHEDULER
        ];

        return queryString.stringifyUrl(
            {
                url: OAUTH_NETWORKS[network],
                query: {
                    ...query,
                    permissions,
                    redirect_to: redirectUrl
                }
            },
            { arrayFormat: 'bracket' }
        );
    };

    return { getOauthUrl };
};

export default useNetworkOauthUrls;
